import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatDuration, formatNumber } from '../i18n.js';
import { useSites } from '../sites.js';
import { hourState } from './SiteCharts.js';
import { Button, LinkButton, Notice } from './ui.js';

type Range = '24h' | '7d' | '30d' | '90d' | '365d';
type Incident = Schemas['Incident'];
const PERIODS: readonly Range[] = ['24h', '7d', '30d', '90d', '365d'];
const RANGE_MS: Record<Range, number> = {
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
  '90d': 90 * 86_400_000,
  '365d': 365 * 86_400_000,
};
/** Above this the home page counts as slow, unless the monitor says otherwise (checks/http). */
const SLOW_MS = 3000;

/** The clock, ticking every `ms` milliseconds. */
function useNow(ms: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** The site with its monitors; the home page monitor is the http one on the site address. */
function useHomeMonitor(siteId: string) {
  const site = useQuery({
    queryKey: ['site', siteId, 'detail'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}', { params: { path: { siteId } } })),
    refetchInterval: 30_000,
  });
  const home = site.data?.monitors.find((m) => m.type === 'http' && m.url === site.data.url);
  return { site: site.data, home };
}

function useUptime(siteId: string, range: Range, enabled = true) {
  return useQuery({
    queryKey: ['site', siteId, 'uptime', range],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } })),
    enabled,
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
  });
}

/** The second check location (T-67); the same query as its card, so one request. */
export function useLocations(siteId: string) {
  const locations = useQuery({
    queryKey: ['site', siteId, 'locations'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/locations', { params: { path: { siteId } } })),
  });
  return { ...locations, running: !!locations.data?.available && !!locations.data.active };
}

/** Seconds as "0,56 s". */
const seconds = (ms: number, language: string) => `${formatNumber(ms / 1000, language, 2)} s`;

/**
 * The head of the availability page (site-incidents.html .hd): how often we check and – only on
 * plans with a running second location (audit) – from where; a test message, pausing, the alerts
 * and the settings (T-98), their results below.
 */
export function AvailabilityHead({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const { site, home } = useHomeMonitor(siteId);
  const { running: twoLocations } = useLocations(siteId);
  const paused = site?.status === 'paused';
  // The recipients are read on click only: the page itself needs no extra request.
  const test = useMutation({
    mutationFn: async () => {
      const { items } = await unwrap(api.GET('/alert-channels'));
      const targets = items.filter(
        (c) => c.verified && c.enabled && c.receives.includes('critical'),
      );
      const results = await Promise.allSettled(
        targets.map((c) =>
          unwrap(
            api.POST('/alert-channels/{channelId}/test', { params: { path: { channelId: c.id } } }),
          ),
        ),
      );
      const sent = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (sent === 0 && failed) throw failed.reason;
      return { sent, none: targets.length === 0 };
    },
  });
  const pause = useMutation({
    mutationFn: (status: 'active' | 'paused') =>
      unwrap(api.PATCH('/sites/{siteId}', { params: { path: { siteId } }, body: { status } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('availability.title')}</h1>
          <div className="sub">
            {home ? (
              <span>
                {t('availability.checkEvery', {
                  interval: formatDuration(home.intervalSeconds, i18n.language),
                })}
                {twoLocations ? ` · ${t('availability.fromTwo')}` : null}
              </span>
            ) : null}
          </div>
        </div>
        <div className="acts">
          <Button variant="secondary" size="sm" busy={test.isPending} onClick={() => test.mutate()}>
            {t('availability.actions.test')}
          </Button>
          {site ? (
            <Button
              variant="secondary"
              size="sm"
              busy={pause.isPending}
              onClick={() => pause.mutate(paused ? 'active' : 'paused')}
            >
              {t(paused ? 'availability.actions.resume' : 'availability.actions.pause')}
            </Button>
          ) : null}
          <LinkButton to="/notifications" variant="ghost" size="sm">
            {t('availability.notifications')}
          </LinkButton>
          <LinkButton to={`/sites/${siteId}/settings`} variant="ghost" size="sm">
            {t('siteNav.settings')}
          </LinkButton>
        </div>
      </div>
      <div role="status" className="notes empty:hidden">
        {test.data ? (
          test.data.none ? (
            <Notice kind="warning">
              {t('availability.actions.noRecipients')}{' '}
              <Link to="/notifications">{t('availability.notifications')}</Link>
            </Notice>
          ) : (
            <Notice kind="success">
              {t('availability.actions.sent', { count: test.data.sent })}
            </Notice>
          )
        ) : null}
        {test.isError ? <Notice kind="error">{errorMessage(test.error, t)}</Notice> : null}
        {pause.isError ? <Notice kind="error">{errorMessage(pause.error, t)}</Notice> : null}
        {paused ? <Notice kind="info">{t('availability.actions.paused')}</Notice> : null}
      </div>
    </>
  );
}

/** State now, the next check and the last 24 hours (T-91, site-incidents.html .tiles3). */
export function StatusTiles({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const now = useNow(1000);
  const { site, home } = useHomeMonitor(siteId);
  const hours = useSites().data?.items.find((s) => s.id === siteId)?.uptimeHours ?? [];
  if (!site || !home) return null;
  const paused = site.status === 'paused';
  const state = paused ? 'paused' : home.state;
  const since = home.stateChangedAt ? now - Date.parse(home.stateChangedAt) : null;
  const left = Math.max(0, Date.parse(home.nextRunAt) - now);
  const slowHours = hours.filter((h) => hourState(h) === 'degraded').length;
  const downHours = hours.filter((h) => h.down).length;
  return (
    <dl className="tiles3">
      <div className="stat">
        <dt className="k">{t('availability.now')}</dt>
        <dd className={`v ${state}`}>{t(`status.${state}`)}</dd>
        {since !== null && !paused ? (
          <dd className="d">
            {t(state === 'up' ? 'availability.upSince' : 'availability.since', {
              duration: formatDuration(since / 1000, i18n.language),
            })}
          </dd>
        ) : null}
      </div>
      <div className="stat">
        <dt className="k">{t('availability.next')}</dt>
        <dd className="v">
          {paused
            ? t('status.paused')
            : left === 0
              ? t('availability.running')
              : t('availability.inSeconds', { count: Math.ceil(left / 1000) })}
        </dd>
        <dd className="d">
          {t('availability.every', {
            interval: formatDuration(home.intervalSeconds, i18n.language),
          })}
        </dd>
      </div>
      <div className="stat">
        <dt className="k">{t('availability.last24h')}</dt>
        <dd className="v">{t('availability.outageHours', { count: downHours })}</dd>
        <dd className="d">{t('availability.slowHours', { count: slowHours })}</dd>
      </div>
    </dl>
  );
}

/** Outages within the window: how many and how long, ongoing ones up to now. */
function outagesIn(incidents: readonly Incident[], since: number, now: number) {
  const hits = incidents.filter(
    (i) => i.kind === 'downtime' && (i.resolvedAt ? Date.parse(i.resolvedAt) : now) >= since,
  );
  const total = hits.reduce(
    (n, i) =>
      n +
      Math.max(
        0,
        (i.resolvedAt ? Date.parse(i.resolvedAt) : now) - Math.max(since, Date.parse(i.startedAt)),
      ) /
        1000,
    0,
  );
  return { count: hits.length, total };
}

/** Uptime per period with its outages; periods beyond the plan point to the plans (T-91). */
export function PeriodStrip({
  siteId,
  incidents,
}: {
  siteId: string;
  incidents: readonly Incident[];
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const first = useUptime(siteId, '24h');
  const allowed = first.data?.allowedRanges ?? [];
  // One daily series for the longest period of the plan; the shorter ones are its last days.
  const longest = [...PERIODS].reverse().find((r) => r !== '24h' && allowed.includes(r));
  const daily = useUptime(siteId, longest ?? '7d', longest !== undefined);
  const pctOf = (range: Range): number | null => {
    if (range === '24h') return first.data?.uptimePct ?? null;
    if (!daily.data) return null;
    const since = Date.now() - RANGE_MS[range];
    const days = daily.data.series.filter((p) => Date.parse(p.t) >= since - 86_400_000 / 2);
    const checks = days.reduce((n, p) => n + p.checks, 0);
    const failed = days.reduce((n, p) => n + p.failed, 0);
    return checks > 0 ? Math.round(((checks - failed) / checks) * 10000) / 100 : null;
  };
  if (!first.data) return null;
  const now = Date.now();
  const locked = PERIODS.some((r) => !allowed.includes(r));
  return (
    <section className="card" aria-labelledby={titleId}>
      <h2 id={titleId}>{t('availability.periods')}</h2>
      <div className="periods">
        {PERIODS.map((range) => {
          const label = t(`availability.range.${range}`);
          if (!allowed.includes(range))
            return (
              <div key={range} className="lock">
                <span>{label}</span>
                <b>–</b>
                <span>{t('availability.locked')}</span>
              </div>
            );
          const pct = pctOf(range);
          const out = outagesIn(incidents, now - RANGE_MS[range], now);
          return (
            <div key={range}>
              <span>{label}</span>
              <b>{pct === null ? '–' : `${formatNumber(pct, i18n.language, 2)} %`}</b>
              <span>
                {out.count === 0
                  ? t('availability.noOutages')
                  : t('availability.outages', {
                      count: out.count,
                      duration: formatDuration(out.total, i18n.language),
                    })}
              </span>
            </div>
          );
        })}
      </div>
      {locked ? (
        <div className="upsell">
          <span>{t('site.chart.locked')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('availability.plans')}
          </LinkButton>
        </div>
      ) : null}
    </section>
  );
}

/** Hetzner sites of the probe as town names; other region codes stay unnamed. */
const TOWNS: Record<string, string> = {
  fsn1: 'Falkenstein',
  nbg1: 'Nürnberg',
  hel1: 'Helsinki',
};
const townOf = (region: string | null) =>
  Object.entries(TOWNS).find(([code]) => region?.includes(code))?.[1] ?? null;

type Line = { key: 'primary' | 'secondary'; points: { t: string; ms: number }[] };

/**
 * Response time of the home page (T-91, site-incidents.html .chart): the average, fastest and
 * slowest beside the title, the slow line, and – while the second location runs (T-93) – both
 * locations in one chart, told apart by colour and dash.
 */
export function ResponseChart({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const [range, setRange] = useState<'24h' | '7d'>('24h');
  const { home } = useHomeMonitor(siteId);
  const uptime = useUptime(siteId, range);
  const { running } = useLocations(siteId);
  const byLocation = useQuery({
    queryKey: ['site', siteId, 'response-locations', range],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/response-locations', {
          params: { path: { siteId }, query: { range } },
        }),
      ),
    enabled: running,
  });
  const config = home?.config as { degraded_threshold_ms?: unknown } | undefined;
  const slow =
    typeof config?.degraded_threshold_ms === 'number' ? config.degraded_threshold_ms : SLOW_MS;
  const values = (uptime.data?.series ?? []).flatMap((p) =>
    p.avgResponseMs === null ? [] : [p.avgResponseMs],
  );
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const located = byLocation.data?.locations ?? [];
  const primary = located.find((l) => l.key === 'primary');
  const secondary = running ? located.find((l) => l.key === 'secondary') : undefined;
  // Both locations from their own series; otherwise the home page's checks.
  const lines: Line[] = secondary
    ? located.map((l) => ({
        key: l.key,
        points: l.series.map((p) => ({ t: p.t, ms: p.avgResponseMs })),
      }))
    : [
        {
          key: 'primary',
          points: (uptime.data?.series ?? []).flatMap((p) =>
            p.avgResponseMs === null ? [] : [{ t: p.t, ms: p.avgResponseMs }],
          ),
        },
      ];
  const drawn = lines.filter((l) => l.points.length >= 2);
  const times = drawn.flatMap((l) => l.points.map((p) => Date.parse(p.t)));
  const from = Math.min(...times);
  const to = Math.max(...times);
  const top = Math.max(slow * 1.2, ...drawn.flatMap((l) => l.points.map((p) => p.ms * 1.1)));
  const w = 600;
  const h = 160;
  const x = (iso: string) => ((Date.parse(iso) - from) * w) / Math.max(1, to - from);
  const y = (v: number) => 8 + (1 - v / top) * (h - 16);
  const town = townOf(secondary?.region ?? null);
  const name = (key: Line['key']) =>
    key === 'primary'
      ? t('availability.byLocation.primary')
      : town
        ? t('availability.byLocation.secondaryIn', { town })
        : t('availability.byLocation.secondary');
  const a = primary?.avgResponseMs ?? null;
  const b = secondary?.avgResponseMs ?? null;
  // The comparison of both locations comes in addition to the verdict on the speed, never instead.
  const comparison =
    secondary && a !== null && b !== null
      ? t(
          `availability.byLocation.${
            b > a * 1.5 && b - a > 300
              ? 'slowerThere'
              : a > b * 1.5 && a - b > 300
                ? 'fasterThere'
                : 'similar'
          }`,
        )
      : null;
  const verdict =
    avg === null
      ? null
      : t(avg < 1000 ? 'availability.fast' : avg < slow ? 'availability.ok' : 'availability.slow', {
          value: seconds(slow, i18n.language),
        });
  const figures = values.length
    ? [
        `${t('availability.avg')} ${seconds(avg ?? 0, i18n.language)}`,
        `${t('availability.fastest')} ${seconds(Math.min(...values), i18n.language)}`,
        `${t('availability.slowest')} ${seconds(Math.max(...values), i18n.language)}`,
      ].join(' · ')
    : null;
  return (
    <section className="card chart" aria-labelledby={titleId}>
      <div className="hd">
        <h2 id={titleId}>
          {t('availability.response')} {figures ? <small>{figures}</small> : null}
        </h2>
        <div className="ranges" role="group" aria-label={t('availability.rangeLabel')}>
          {(['24h', '7d'] as const).map((r) => (
            <button key={r} type="button" aria-pressed={range === r} onClick={() => setRange(r)}>
              {t(`availability.range.${r}`)}
            </button>
          ))}
        </div>
      </div>
      {drawn.length === 0 ? (
        <p className="empty-chart">{t('site.chart.empty')}</p>
      ) : (
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={
            secondary
              ? t('availability.byLocation.chartSr', {
                  primary: a === null ? '–' : seconds(a, i18n.language),
                  secondary: b === null ? '–' : seconds(b, i18n.language),
                })
              : t('availability.chartSr', {
                  avg: seconds(avg ?? 0, i18n.language),
                  max: seconds(values.length ? Math.max(...values) : 0, i18n.language),
                })
          }
        >
          <line
            x1="0"
            x2={w}
            y1={y(slow)}
            y2={y(slow)}
            stroke="var(--warn)"
            strokeDasharray="4 4"
          />
          <text
            x="4"
            y={y(slow) - 6}
            fontSize="10"
            fill="var(--warn)"
            fontFamily="JetBrains Mono, monospace"
          >
            {t('availability.slowLine', { value: seconds(slow, i18n.language) })}
          </text>
          {drawn.map((l) => (
            <path
              key={l.key}
              data-line={l.key}
              d={l.points.map((p, n) => `${n === 0 ? 'M' : 'L'}${x(p.t)} ${y(p.ms)}`).join(' ')}
              fill="none"
              stroke={l.key === 'primary' ? 'var(--acc)' : 'var(--fg-3)'}
              strokeWidth={l.key === 'primary' ? 2 : 1.5}
              strokeDasharray={l.key === 'primary' ? undefined : '3 3'}
            />
          ))}
        </svg>
      )}
      <div className="legend">
        {secondary
          ? located.map((l) => (
              <span key={l.key}>
                <i className={l.key} aria-hidden="true" />
                <span>{name(l.key)}</span>{' '}
                <b>
                  {l.avgResponseMs === null
                    ? '–'
                    : t('availability.byLocation.avg', {
                        value: seconds(l.avgResponseMs, i18n.language),
                      })}
                </b>
              </span>
            ))
          : null}
        {comparison ? <span>{comparison}</span> : null}
        {verdict ? <span>{verdict}</span> : null}
      </div>
    </section>
  );
}
